import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { mockResources, mockFAQ } from "../mock";
import { ArrowRight, Download, FileText, HelpCircle, BookOpen } from "lucide-react";

const ResourcesPage = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-excel-steel to-excel-dark py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {t("resourcesTitle")}
            </h1>
            <p className="text-xl max-w-3xl mx-auto leading-relaxed">
              Documentation technique, guides pratiques et réponses à vos questions pour optimiser vos équipements de levage.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            
            {/* Guides & Resources */}
            <div className="lg:col-span-2 space-y-8">
              <div className="space-y-6">
                <div className="flex items-center space-x-3">
                  <BookOpen className="h-8 w-8 text-excel-light-red" />
                  <h2 className="text-3xl font-bold text-excel-dark">
                    Guides techniques
                  </h2>
                </div>
                <p className="text-lg text-gray-600">
                  Téléchargez nos guides complets pour optimiser l'utilisation et la maintenance de vos équipements.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {mockResources.map((resource) => (
                  <Card key={resource.id} className="group hover:shadow-lg transition-all border-l-4 border-l-excel-light-red">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="bg-excel-light-red p-3 rounded-lg">
                          <FileText className="h-6 w-6 text-white" />
                        </div>
                        <Badge variant="secondary">{resource.type}</Badge>
                      </div>
                      <CardTitle className="text-excel-dark">
                        {t(resource.title)}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <CardDescription className="text-gray-600">
                        {resource.description}
                      </CardDescription>
                      
                      <Button 
                        className="w-full bg-excel-light-red hover:bg-excel-red text-white"
                        onClick={() => {
                          // Mock download action
                          console.log(`Downloading: ${resource.title}`);
                        }}
                      >
                        <Download className="mr-2 h-4 w-4" />
                        Télécharger
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Additional Resources */}
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-excel-dark">
                  Ressources supplémentaires
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card className="border-l-4 border-l-excel-light-red">
                    <CardHeader>
                      <CardTitle className="text-excel-dark">Vidéos de formation</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="mb-4">
                        Accédez à notre bibliothèque de vidéos explicatives pour la maintenance et l'utilisation sécuritaire.
                      </CardDescription>
                      <Button variant="outline" className="border-excel-steel text-excel-steel hover:bg-excel-steel hover:text-white">
                        Voir les vidéos
                      </Button>
                    </CardContent>
                  </Card>

                  <Card className="border-l-4 border-l-excel-light-red">
                    <CardHeader>
                      <CardTitle className="text-excel-dark">Calculateurs en ligne</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="mb-4">
                        Outils de calcul pour dimensionner vos équipements et estimer vos besoins.
                      </CardDescription>
                      <Button variant="outline" className="border-excel-steel text-excel-steel hover:bg-excel-steel hover:text-white">
                        Utiliser les outils
                      </Button>
                    </CardContent>
                  </Card>

                  <Card className="border-l-4 border-l-excel-light-red">
                    <CardHeader>
                      <CardTitle className="text-excel-dark">Bulletin technique</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="mb-4">
                        Abonnez-vous à notre bulletin mensuel pour les dernières innovations et conseils.
                      </CardDescription>
                      <Button variant="outline" className="border-excel-steel text-excel-steel hover:bg-excel-steel hover:text-white">
                        S'abonner
                      </Button>
                    </CardContent>
                  </Card>

                  <Card className="border-l-4 border-l-excel-light-red">
                    <CardHeader>
                      <CardTitle className="text-excel-dark">Support technique</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="mb-4">
                        Contactez directement nos ingénieurs pour assistance technique personnalisée.
                      </CardDescription>
                      <Button variant="outline" className="border-excel-steel text-excel-steel hover:bg-excel-steel hover:text-white">
                        Contacter le support
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>

            {/* FAQ Sidebar */}
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <HelpCircle className="h-6 w-6 text-excel-light-red" />
                  <h2 className="text-2xl font-bold text-excel-dark">
                    Questions fréquentes
                  </h2>
                </div>
              </div>

              <Card className="border-l-4 border-l-excel-light-red">
                <CardContent className="p-0">
                  <Accordion type="single" collapsible className="w-full">
                    {mockFAQ.map((faq) => (
                      <AccordionItem key={faq.id} value={`faq-${faq.id}`}>
                        <AccordionTrigger className="px-6 py-4 text-left hover:bg-gray-50">
                          <span className="font-medium text-excel-dark">
                            {t(faq.question)}
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="px-6 pb-4">
                          <p className="text-gray-600 leading-relaxed">
                            {faq.answer}
                          </p>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>

              {/* Quick Contact */}
              <Card className="bg-excel-light-gray">
                <CardHeader>
                  <CardTitle className="text-excel-dark">
                    Besoin d'aide personnalisée ?
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-gray-600">
                    Nos experts sont disponibles pour répondre à vos questions spécifiques.
                  </p>
                  
                  <div className="space-y-3">
                    <div className="text-sm">
                      <p className="font-medium text-excel-dark">Support technique</p>
                      <p className="text-gray-600">1-800-EXCEL-24</p>
                    </div>
                    
                    <div className="text-sm">
                      <p className="font-medium text-excel-dark">Email</p>
                      <p className="text-gray-600">support@excel-levage.com</p>
                    </div>
                    
                    <div className="text-sm">
                      <p className="font-medium text-excel-dark">Heures d'ouverture</p>
                      <p className="text-gray-600">Lun-Ven: 8h-17h</p>
                    </div>
                  </div>
                  
                  <Button 
                    asChild 
                    className="w-full bg-excel-light-red hover:bg-excel-red text-white"
                  >
                    <Link to="/contact">
                      Nous contacter
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>

              {/* Training */}
              <Card className="border-l-4 border-l-excel-light-red">
                <CardHeader>
                  <CardTitle className="text-excel-dark">
                    Formation & Certification
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-gray-600">
                    Programmes de formation certifiés pour vos opérateurs et techniciens.
                  </p>
                  
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li>• Formation opérateurs</li>
                    <li>• Certification sécurité</li>
                    <li>• Maintenance préventive</li>
                    <li>• Inspection réglementaire</li>
                  </ul>
                  
                  <Button 
                    variant="outline" 
                    className="w-full border-excel-steel text-excel-steel hover:bg-excel-steel hover:text-white"
                  >
                    En savoir plus
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Resources Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-excel-dark mb-4">
              Centre de connaissances
            </h2>
            <p className="text-xl text-gray-600">
              Explorez nos ressources complètes pour optimiser vos opérations
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="text-center border-l-4 border-l-excel-light-red">
              <CardHeader>
                <div className="bg-excel-light-red w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-excel-dark">Documentation technique</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Manuels d'utilisation, schémas techniques et spécifications détaillées.
                </p>
                <Button variant="outline" className="border-excel-steel text-excel-steel hover:bg-excel-steel hover:text-white">
                  Accéder
                </Button>
              </CardContent>
            </Card>

            <Card className="text-center border-l-4 border-l-excel-light-red">
              <CardHeader>
                <div className="bg-excel-light-red w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <BookOpen className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-excel-dark">Blog technique</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Articles d'experts sur les tendances et innovations en levage industriel.
                </p>
                <Button variant="outline" className="border-excel-steel text-excel-steel hover:bg-excel-steel hover:text-white">
                  Lire le blog
                </Button>
              </CardContent>
            </Card>

            <Card className="text-center border-l-4 border-l-excel-light-red">
              <CardHeader>
                <div className="bg-excel-light-red w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <HelpCircle className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-excel-dark">Base de connaissances</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Solutions aux problèmes courants et guides de dépannage.
                </p>
                <Button variant="outline" className="border-excel-steel text-excel-steel hover:bg-excel-steel hover-text-white">
                  Explorer
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-excel-steel">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Une question spécifique ?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Notre équipe d'experts est à votre disposition pour vous accompagner dans tous vos projets.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild 
              size="lg"
              className="bg-excel-light-red hover:bg-excel-red text-white"
            >
              <Link to="/contact">
                Télécharger nos guides
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button 
              asChild 
              variant="outline" 
              size="lg"
              className="border-white text-white hover:bg-white hover:text-excel-steel"
            >
              <Link to="/about">En savoir plus sur nous</Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ResourcesPage;